import React, { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  ChevronRight,
  CloudOff,
  Download,
  FileAudio,
  FileText,
  FileVideo,
  ImageOff,
  Info,
  LucideIcon,
  Trash2,
  X,
} from 'lucide-react';
import { MediaAsset } from '../../types/media';
import { mediaService } from '../../services/mediaService';
import { useAuthBlobUrl } from '../../hooks/useAuthBlobUrl';
import { ConfirmModal } from '../common/ConfirmModal';
import { mediaTypeIcon } from './MediaTypeIcon';
import { MEDIA_LOAD_ERROR_TITLE, MediaLoadErrorKind } from '../../utils/mediaLoadError';

interface MediaViewerProps {
  media: MediaAsset | null;
  onClose: () => void;
  onDelete?: (media: MediaAsset) => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  allowDelete?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Shared bits                                                        */
/* ------------------------------------------------------------------ */

const TOP_BAR = 'calc(3.5rem + env(safe-area-inset-top, 0px))';
const SWIPE_DISTANCE = 70;
const DISMISS_DISTANCE = 110;

const DarkSpinner: React.FC = () => (
  <div className="flex h-full items-center justify-center">
    <span className="h-7 w-7 animate-spin rounded-full border-2 border-white/15 border-t-white/80" aria-label="Loading" />
  </div>
);

const LoadError: React.FC<{ icon: LucideIcon; label: string }> = ({ icon: Icon, label }) => (
  <div className="flex flex-col items-center justify-center gap-3 text-white/50">
    <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/[0.06]">
      <Icon className="h-6 w-6" strokeWidth={1.5} />
    </span>
    <p className="text-[13px]">{label}</p>
  </div>
);

/** Missing file (404) and unreachable storage (503) get their own message; anything else the generic one */
const MediaLoadFailure: React.FC<{ kind?: MediaLoadErrorKind | null; icon: LucideIcon; label: string }> = ({
  kind,
  icon,
  label,
}) => {
  if (kind === 'missing') return <LoadError icon={icon} label={MEDIA_LOAD_ERROR_TITLE.missing} />;
  if (kind === 'unavailable') return <LoadError icon={CloudOff} label={MEDIA_LOAD_ERROR_TITLE.unavailable} />;
  return <LoadError icon={icon} label={label} />;
};

const ViewerButton: React.FC<{
  label: string;
  title?: string;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ label, title, onClick, active = false, danger = false, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    aria-pressed={active || undefined}
    title={title ?? label}
    className={`inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
      active
        ? 'bg-white/15 text-white'
        : danger
          ? 'text-white/80 hover:bg-primary-600/20 hover:text-primary-400'
          : 'text-white/80 hover:bg-white/10 hover:text-white'
    }`}
  >
    {children}
  </button>
);

/* ------------------------------------------------------------------ */
/*  Sub-components for each media type                                 */
/* ------------------------------------------------------------------ */

const ImageViewer: React.FC<{ mediaId: string; alt: string }> = ({ mediaId, alt }) => {
  const { url, loading, error, errorKind } = useAuthBlobUrl(mediaId);

  if (loading) return <DarkSpinner />;
  if (error) return <MediaLoadFailure kind={errorKind} icon={ImageOff} label="Failed to load image" />;
  if (!url) return null;

  return (
    <img
      src={url}
      alt={alt}
      className="max-h-full max-w-full select-none object-contain"
      draggable={false}
    />
  );
};

const VideoViewer: React.FC<{ mediaId: string }> = ({ mediaId }) => {
  const { url, loading, error, errorKind } = useAuthBlobUrl(mediaId);

  if (loading) return <DarkSpinner />;
  if (error) return <MediaLoadFailure kind={errorKind} icon={FileVideo} label="Failed to load video" />;
  if (!url) return null;

  return (
    <video
      src={url}
      controls
      autoPlay
      playsInline
      className="max-h-full max-w-full rounded-xl"
      style={{ maxHeight: 'calc(100dvh - 140px)' }}
    />
  );
};

const AudioViewer: React.FC<{ mediaId: string; name: string }> = ({ mediaId, name }) => {
  const { url, loading, error, errorKind } = useAuthBlobUrl(mediaId);

  if (loading) return <DarkSpinner />;
  if (error) return <MediaLoadFailure kind={errorKind} icon={FileAudio} label="Failed to load audio" />;
  if (!url) return null;

  return (
    <div className="flex w-full max-w-md flex-col items-center gap-5 px-4">
      <span className="inline-flex h-28 w-28 items-center justify-center rounded-[28px] bg-white/[0.06] ring-1 ring-white/10 sm:h-36 sm:w-36">
        <FileAudio className="h-10 w-10 text-white/60" strokeWidth={1.25} />
      </span>
      <p className="max-w-full truncate text-center text-[13px] text-white/70">{name}</p>
      <audio src={url} controls autoPlay className="w-full" />
    </div>
  );
};

const PdfViewer: React.FC<{ mediaId: string }> = ({ mediaId }) => {
  const { url, loading, error, errorKind } = useAuthBlobUrl(mediaId);

  if (loading) return <DarkSpinner />;
  if (error) return <MediaLoadFailure kind={errorKind} icon={FileText} label="Failed to load PDF" />;
  if (!url) return null;

  return (
    <iframe
      src={url}
      title="PDF Viewer"
      className="h-full w-full rounded-xl bg-white"
      style={{ maxWidth: '900px', minHeight: '400px' }}
    />
  );
};

const DocumentPlaceholder: React.FC<{ media: MediaAsset; onDownload: () => void }> = ({ media, onDownload }) => {
  const Icon = mediaTypeIcon(media.mimeType);
  return (
    <div className="flex max-w-sm flex-col items-center gap-3 px-6 text-center">
      <span className="inline-flex h-24 w-24 items-center justify-center rounded-[24px] bg-white/[0.06] ring-1 ring-white/10">
        <Icon className="h-9 w-9 text-white/60" strokeWidth={1.25} />
      </span>
      <p className="mt-2 max-w-full break-words text-[15px] text-white/90">{media.originalName}</p>
      <p className="text-[12px] text-white/40">
        {media.mimeType} · {mediaService.formatFileSize(media.fileSize)}
      </p>
      <button
        type="button"
        onClick={onDownload}
        className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-full bg-white px-4 text-[13px] font-medium text-ink transition-colors hover:bg-white/90"
      >
        <Download className="h-4 w-4" strokeWidth={1.75} />
        Download
      </button>
      <p className="text-[12px] text-white/40">No preview</p>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main MediaViewer                                                   */
/* ------------------------------------------------------------------ */

export const MediaViewer: React.FC<MediaViewerProps> = ({
  media,
  onClose,
  onDelete,
  onNavigate,
  hasPrev = false,
  hasNext = false,
  allowDelete = false,
}) => {
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [drag, setDrag] = useState<{ dx: number; dy: number; active: boolean }>({ dx: 0, dy: 0, active: false });
  const touch = useRef<{ x: number; y: number; axis: 'x' | 'y' | null } | null>(null);

  // Keyboard navigation
  useEffect(() => {
    if (!media) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onNavigate?.('prev');
      if (e.key === 'ArrowRight' && hasNext) onNavigate?.('next');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [media, onClose, onNavigate, hasPrev, hasNext]);

  // Lock body scroll
  useEffect(() => {
    if (!media) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [media]);

  // A new item starts centred
  useEffect(() => {
    touch.current = null;
    setDrag({ dx: 0, dy: 0, active: false });
  }, [media?.id]);

  const handleDownload = useCallback(async () => {
    if (!media) return;
    try {
      // Fetch with auth and trigger download
      const token = (await import('../../utils/auth')).tokenStorage.get();
      const res = await fetch(mediaService.getViewUrl(media.id), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = media.originalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback
      window.open(mediaService.getDownloadUrl(media.id), '_blank');
    }
  }, [media]);

  /* Swipe left/right to move between items, down to close. Players and PDFs keep their own gestures. */
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1 || (e.target as HTMLElement).closest('video, audio, iframe, button')) {
      touch.current = null;
      return;
    }
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, axis: null };
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const start = touch.current;
    if (!start) return;
    if (e.touches.length !== 1) {
      touch.current = null;
      setDrag({ dx: 0, dy: 0, active: false });
      return;
    }
    const dx = e.touches[0].clientX - start.x;
    const dy = e.touches[0].clientY - start.y;
    if (!start.axis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      start.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (start.axis === 'x') {
      // Resist at the ends of the list
      const blocked = (dx > 0 && !hasPrev) || (dx < 0 && !hasNext);
      setDrag({ dx: blocked ? dx / 4 : dx, dy: 0, active: true });
    } else if (start.axis === 'y') {
      setDrag({ dx: 0, dy: Math.max(0, dy), active: true });
    }
  };

  const onTouchEnd = () => {
    const start = touch.current;
    touch.current = null;
    if (!start?.axis) return;
    if (start.axis === 'x') {
      if (drag.dx > SWIPE_DISTANCE && hasPrev) {
        setDrag({ dx: 0, dy: 0, active: false });
        onNavigate?.('prev');
        return;
      }
      if (drag.dx < -SWIPE_DISTANCE && hasNext) {
        setDrag({ dx: 0, dy: 0, active: false });
        onNavigate?.('next');
        return;
      }
    } else if (drag.dy > DISMISS_DISTANCE) {
      onClose();
      return;
    }
    setDrag({ dx: 0, dy: 0, active: false });
  };

  if (!media) return null;

  const isImage = mediaService.isImageFile(media.mimeType);
  const isVideo = mediaService.isVideoFile(media.mimeType);
  const isAudio = mediaService.isAudioFile(media.mimeType);
  const isPdf = media.mimeType === 'application/pdf';

  const renderContent = () => {
    if (isImage) return <ImageViewer mediaId={media.id} alt={media.originalName} />;
    if (isVideo) return <VideoViewer mediaId={media.id} />;
    if (isAudio) return <AudioViewer mediaId={media.id} name={media.originalName} />;
    if (isPdf) return <PdfViewer mediaId={media.id} />;
    return <DocumentPlaceholder media={media} onDownload={handleDownload} />;
  };

  const getEntityName = () => {
    if (media.stakeholder) return `${media.stakeholder.firstName} ${media.stakeholder.lastName}`.trim();
    if (media.organization) return media.organization.name;
    return 'Uncategorized';
  };

  const details: Array<{ label: string; value: React.ReactNode; wrap?: boolean }> = [
    { label: 'Filename', value: media.originalName, wrap: true },
    { label: 'Size', value: mediaService.formatFileSize(media.fileSize) },
    { label: 'Type', value: media.mimeType },
    { label: 'Uploaded', value: new Date(media.createdAt).toLocaleString() },
    { label: 'Entity', value: getEntityName() },
    ...(media.uploader ? [{ label: 'Uploaded by', value: media.uploader.email }] : []),
  ];

  const dismissProgress = Math.min(drag.dy / 400, 0.6);

  const viewer = (
    <div
      className="fixed inset-0 z-50 animate-overlay-in overflow-hidden font-ui text-white antialiased"
      style={{ backgroundColor: `rgba(0, 0, 0, ${1 - dismissProgress})` }}
      role="dialog"
      aria-modal="true"
      aria-label={media.originalName}
    >
      {/* Content */}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-[padding] ${isPdf ? '' : 'touch-none'} ${showInfo ? 'sm:pr-80' : ''}`}
        style={{
          paddingTop: isImage ? 0 : TOP_BAR,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div
          key={media.id}
          className={`flex h-full w-full items-center justify-center ${
            isPdf ? 'px-2 pb-2 sm:px-12 sm:pb-6' : isImage ? '' : 'p-4 sm:p-8'
          }`}
          style={{
            transform: `translate3d(${drag.dx}px, ${drag.dy}px, 0) scale(${1 - dismissProgress / 3})`,
            transition: drag.active ? 'none' : 'transform 240ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {renderContent()}
        </div>
      </div>

      {/* Top bar */}
      <div
        className={`absolute inset-x-0 top-0 z-20 border-b border-white/[0.06] bg-black/40 backdrop-blur-xl backdrop-saturate-150 transition-opacity ${
          drag.dy > 0 ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex h-14 items-center gap-1 px-2 sm:px-3">
          <ViewerButton label="Close" title="Close (Esc)" onClick={onClose}>
            <X className="h-5 w-5" strokeWidth={1.75} />
          </ViewerButton>
          <div className="min-w-0 flex-1 px-1.5">
            <p className="truncate text-[14px] leading-tight text-white">{media.originalName}</p>
            <p className="mt-0.5 truncate text-[12px] leading-tight text-white/50">
              {mediaService.formatFileSize(media.fileSize)} · {media.mimeType}
            </p>
          </div>
          <ViewerButton label="Info" active={showInfo} onClick={() => setShowInfo(v => !v)}>
            <Info className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </ViewerButton>
          <ViewerButton label="Download" onClick={handleDownload}>
            <Download className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </ViewerButton>
          {allowDelete && (
            <ViewerButton label="Delete" danger onClick={() => setConfirmOpen(true)}>
              <Trash2 className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </ViewerButton>
          )}
        </div>
      </div>

      {/* Previous / next (swipe on phones) */}
      {hasPrev && (
        <button
          type="button"
          onClick={() => onNavigate?.('prev')}
          aria-label="Previous"
          title="Previous (←)"
          className="absolute left-4 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white/80 backdrop-blur-xl transition-colors hover:bg-white/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 sm:inline-flex"
        >
          <ChevronLeft className="h-5 w-5" strokeWidth={1.75} />
        </button>
      )}
      {hasNext && (
        <button
          type="button"
          onClick={() => onNavigate?.('next')}
          aria-label="Next"
          title="Next (→)"
          className={`absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white/80 backdrop-blur-xl transition-[right,background-color] hover:bg-white/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 sm:inline-flex ${
            showInfo ? 'right-4 sm:right-[21rem]' : 'right-4'
          }`}
        >
          <ChevronRight className="h-5 w-5" strokeWidth={1.75} />
        </button>
      )}

      {/* Details: bottom panel on phones, right panel from sm */}
      {showInfo && (
        <div
          className="absolute inset-x-0 bottom-0 z-10 max-h-[55%] animate-fade-up overflow-y-auto rounded-t-[24px] border-t border-white/10 bg-neutral-900/85 px-5 pt-4 backdrop-blur-xl sm:inset-x-auto sm:right-0 sm:top-0 sm:max-h-none sm:w-80 sm:rounded-none sm:border-l sm:border-t-0"
          style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
        >
          <div className="hidden sm:block" style={{ height: TOP_BAR }} />
          <h3 className="mb-2 text-[15px] font-normal text-white">Details</h3>
          <dl className="divide-y divide-white/[0.08]">
            {details.map(row => (
              <div key={row.label} className="flex items-baseline justify-between gap-4 py-2.5 sm:block">
                <dt className="flex-shrink-0 text-[12px] text-white/40 sm:mb-0.5">{row.label}</dt>
                <dd
                  className={`min-w-0 text-right text-[13px] text-white/90 sm:text-left ${
                    row.wrap ? 'break-all' : 'truncate sm:whitespace-normal sm:break-words'
                  }`}
                >
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );

  return (
    <>
      {typeof document !== 'undefined' ? createPortal(viewer, document.body) : viewer}

      {/* Delete confirm modal */}
      <ConfirmModal
        open={confirmOpen}
        title="Delete file"
        message={<span>Delete “{media.originalName}”? This can’t be undone.</span>}
        confirmText="Delete"
        loading={deleting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          try {
            setDeleting(true);
            await mediaService.deleteMediaAsset(media.id);
            setConfirmOpen(false);
            onDelete?.(media);
            onClose();
          } catch {
            // Error handled silently
          } finally {
            setDeleting(false);
          }
        }}
      />
    </>
  );
};
